import { writable, derived } from 'svelte/store';
import type { AuthUser } from '@/shared/types';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};
// Create auth store
function createAuthStore() {
  const { subscribe, set, update } = writable<AuthState>(initialState);

  // Load user from localStorage on initialization
  if (typeof window !== 'undefined') {
    const savedUser = localStorage.getItem('neohris_user');
    const savedToken = localStorage.getItem('neohris_token');
    
    if (savedUser && savedToken) {
      try {
        const user = JSON.parse(savedUser);
        set({
          user,
          token: savedToken,
          isAuthenticated: true,
          loading: false,
          error: null,
        });
      } catch (error) {
        clearAuth(set);
      }
    }
  }

  return {
    subscribe,
    
    // Login action
    login: async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
      update(state => ({ ...state, loading: true, error: null }));
      
      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Important for cookies
          body: JSON.stringify({ email, password }),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'Login failed');
        }
        
        if (data.success && data.data?.user) {
          const { user, token } = data.data;
          
          // Save to localStorage
          if (typeof window !== 'undefined') {
            localStorage.setItem('neohris_user', JSON.stringify(user));
            if (token) {
              localStorage.setItem('neohris_token', token);
            }
          }
          
          // Update store
          set({
            user,
            token: token || '',
            isAuthenticated: true,
            loading: false,
            error: null,
          });
          
          return { success: true };
        } else {
          throw new Error(data.error || 'Login failed');
        }
      } catch (error: any) {
        const errorMessage = error.message || 'Login failed';
        update(state => ({ ...state, loading: false, error: errorMessage }));
        return { success: false, error: errorMessage };
      }
    },
    
    // Logout action
    logout: async (): Promise<void> => {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
        });
      } catch (error) {
        // logout API failed, clear local state anyway
      }
      
      // Clear local state regardless of API response
      clearAuth(set);
    },
    
    // Get current user from server
    refreshUser: async (): Promise<{ success: boolean; error?: string }> => {
      update(state => ({ ...state, loading: true, error: null }));
      
      try {
        const response = await fetch('/api/auth/me', {
          credentials: 'include', // Important for cookies
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to get user');
        }
        
        if (data.success && data.data) {
          const user = data.data;
          
          // Save to localStorage
          if (typeof window !== 'undefined') {
            localStorage.setItem('neohris_user', JSON.stringify(user));
          }
          
          // Update store
          update(state => ({
            ...state,
            user,
            isAuthenticated: true,
            loading: false,
            error: null,
          }));
          
          return { success: true };
        } else {
          throw new Error(data.error || 'Failed to get user');
        }
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to refresh user';
        update(state => ({ ...state, loading: false, error: errorMessage }));
        return { success: false, error: errorMessage };
      }
    },
    
    // Clear auth state
    clear: () => clearAuth(set),
    
    // Update user data
    updateUser: (userData: Partial<AuthUser>) => {
      update(state => {
        if (!state.user) return state;
        
        const updatedUser = { ...state.user, ...userData };
        
        // Save to localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('neohris_user', JSON.stringify(updatedUser));
        }
        
        return {
          ...state,
          user: updatedUser,
        };
      });
    },
  };
}

// Helper function to clear auth state
function clearAuth(set: ReturnType<typeof writable<AuthState>>['set']) {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('neohris_user');
    localStorage.removeItem('neohris_token');
  }
  
  set(initialState);
}

// Export auth store
export const authStore = createAuthStore();

// Derived stores for convenience
export const isAuthenticated = derived(authStore, ($authStore) => $authStore.isAuthenticated);
export const currentUser = derived(authStore, ($authStore) => $authStore.user);
export const userRole = derived(authStore, ($authStore) => $authStore.user?.role);
export const userBranchId = derived(authStore, ($authStore) => $authStore.user?.branchId);
export const isLoading = derived(authStore, ($authStore) => $authStore.loading);
export const authError = derived(authStore, ($authStore) => $authStore.error);

// Helper functions for checking permissions
export const isSuperAdmin = derived(authStore, ($authStore) => 
  $authStore.user?.role === 'superadmin'
);

export const isAdmin = derived(authStore, ($authStore) => 
  $authStore.user?.role === 'admin'
);

export const hasBranchAccess = derived(
  [authStore],
  ([$authStore]) => {
    if (!$authStore.user) return false;
    if ($authStore.user.role === 'superadmin') return true;
    return !!$authStore.user.branchId;
  }
);

// Export type for store
export type AuthStore = ReturnType<typeof createAuthStore>;